import React from 'react';
import clsx from 'clsx';

import { KeenIcon } from '@/components/keenicons';
import {
  IMenuItemConfig,
  Menu,
  MenuArrow,
  MenuBadge,
  MenuBullet,
  TMenuConfig,
  MenuHeading,
  MenuIcon,
  MenuItem,
  MenuLabel,
  MenuLink,
  MenuSub,
  MenuTitle
} from '@/components/menu';
import { useMenus } from '@/providers';

interface ExtendedMenuItemConfig extends IMenuItemConfig {
  disabledReason?: string;
}

const SidebarMenu = () => {
  const [menuConfig, setMenuConfig] = React.useState<TMenuConfig | null>(null);
  const { getMenuConfig } = useMenus();

  React.useEffect(() => {
    async function fetchMenu() {
      try {
        const config = await getMenuConfig('primary');
        setMenuConfig(config);
      } catch (error) {
        console.error('Error fetching menu configuration:', error);
        setMenuConfig([]); // Fallback to an empty array on error
      }
    }

    fetchMenu();
  }, [getMenuConfig]);

  // Style and layout constants
  const linkPl = 'ps-[10px]';
  const linkPr = 'pe-[10px]';
  const linkPy = 'py-[6px]';
  const subLinkPy = 'py-[8px]';
  const rightOffset = 'me-[-10px]';
  const iconWidth = 'w-[20px]';
  const iconSize = 'text-lg';
  const accordionLinkPl = 'ps-[10px]';
  const accordionLinkGap = [
    'gap-[10px]',
    'gap-[14px]',
    'gap-[5px]',
    'gap-[5px]',
    'gap-[5px]',
    'gap-[5px]'
  ];
  const accordionPl = [
    'ps-[10px]',
    'ps-[22px]',
    'ps-[22px]',
    'ps-[22px]',
    'ps-[22px]',
    'ps-[22px]'
  ];
  const accordionBorderLeft = [
    'before:start-[20px]',
    'before:start-[32px]',
    'before:start-[32px]',
    'before:start-[32px]',
    'before:start-[32px]'
  ];

  const buildMenu = (items: TMenuConfig) => {
    if (!Array.isArray(items)) {
      console.error('Expected items to be an array, but got:', items);
      return null;
    }
    return items.map((item, index) => {
      if (item.heading) {
        return buildMenuHeading(item, index);
      } else if (item.disabled) {
        return buildMenuItemRootDisabled(item as ExtendedMenuItemConfig, index);
      } else {
        return buildMenuItemRoot(item, index);
      }
    });
  };

  const buildMenuItemRoot = (item: IMenuItemConfig, index: number) => {
    if (item.children) {
      return (
          <MenuItem
              key={index}
              {...(item.toggle && { toggle: item.toggle })}
              {...(item.trigger && { trigger: item.trigger })}
          >
            <MenuLink
                className={clsx(
                    'flex items-center grow cursor-pointer border border-transparent',
                    accordionLinkGap[0],
                    linkPl,
                    linkPr,
                    linkPy
                )}
            >
              <MenuIcon className={clsx('items-start text-gray-500 dark:text-gray-400', iconWidth)}>
                {item.icon && <KeenIcon icon={item.icon} className={iconSize} />}
              </MenuIcon>
              <MenuTitle className="text-sm font-semibold text-gray-700 menu-item-active:text-primary menu-link-hover:!text-primary">
                {item.title}
              </MenuTitle>
              {buildMenuArrow()}
            </MenuLink>
            <MenuSub
                className={clsx(
                    'relative before:absolute before:top-0 before:bottom-0 before:border-l before:border-gray-200',
                    '[&_.MuiCollapse-wrapperInner]:flex [&_.MuiCollapse-wrapperInner]:flex-col [&_.MuiCollapse-wrapperInner]:gap-0.5',
                    accordionBorderLeft[0],
                    accordionPl[0]
                )}
            >
              {buildMenuItemChildren(item.children as TMenuConfig, index, 1)}
            </MenuSub>
          </MenuItem>
      );
    } else {
      return (
          <MenuItem key={index}>
            <MenuLink
                path={item.path}
                className={clsx(
                    'border border-transparent menu-item-active:bg-secondary-active dark:menu-item-active:bg-coal-300 dark:menu-item-active:border-gray-100 menu-item-active:rounded-lg hover:bg-secondary-active dark:hover:bg-coal-300 dark:hover:border-gray-100 hover:rounded-lg',
                    accordionLinkGap[0],
                    linkPy,
                    linkPl,
                    linkPr
                )}
            >
              <MenuIcon
                  className={clsx(
                      'items-start text-gray-500 dark:text-gray-400 menu-item-active:text-primary menu-link-hover:!text-primary',
                      iconWidth
                  )}
              >
                {item.icon && <KeenIcon icon={item.icon} className={iconSize} />}
              </MenuIcon>
              <MenuTitle className="text-sm font-semibold text-gray-700 menu-item-active:text-primary menu-link-hover:!text-primary">
                {item.title}
              </MenuTitle>
            </MenuLink>
          </MenuItem>
      );
    }
  };

  const buildMenuItemRootDisabled = (item: ExtendedMenuItemConfig, index: number) => {
    return (
        <MenuItem key={index}>
          <MenuLabel
              className={clsx(
                  'border border-transparent cursor-default opacity-50',
                  accordionLinkGap[0],
                  linkPy,
                  linkPl,
                  linkPr
              )}
          >
            <MenuIcon className={clsx('items-start text-gray-500 dark:text-gray-400', iconWidth)}>
              {item.icon && <KeenIcon icon={item.icon} className={iconSize} />}
            </MenuIcon>
            <MenuTitle className="text-sm font-semibold text-gray-700">{item.title}</MenuTitle>
            {item.disabled && buildMenuDisabled()}
            {item.disabledReason && (
                <div className="text-xs text-red-500 mt-1">{item.disabledReason}</div>
            )}
          </MenuLabel>
        </MenuItem>
    );
  };

  const buildMenuItemChildren = (items: TMenuConfig, index: number, level: number = 0) => {
    return items.map((item, idx) => {
      if (item.disabled) {
        return buildMenuItemChildDisabled(item as ExtendedMenuItemConfig, idx, level);
      } else {
        return buildMenuItemChild(item, idx, level);
      }
    });
  };

  const buildMenuItemChild = (item: IMenuItemConfig, index: number, level: number = 0) => {
    if (item.children) {
      return (
          <MenuItem
              key={index}
              {...(item.toggle && { toggle: item.toggle })}
              {...(item.trigger && { trigger: item.trigger })}
              className={clsx(item.collapse && 'flex-col-reverse')}
          >
            <MenuLink
                className={clsx(
                    'border border-transparent grow cursor-pointer',
                    accordionLinkGap[level],
                    accordionLinkPl,
                    linkPr,
                    subLinkPy
                )}
            >
              {buildMenuBullet()}
              {item.collapse ? (
                  <MenuTitle className="text-2sm font-medium text-gray-500 dark:text-gray-400">
                    <span className="hidden menu-item-show:!flex">{item.collapseTitle}</span>
                    <span className="flex menu-item-show:hidden">{item.expandTitle}</span>
                  </MenuTitle>
              ) : (
                  <MenuTitle className="text-2sm font-medium mr-1 text-gray-700 menu-item-active:text-primary menu-item-active:font-semibold menu-link-hover:!text-primary">
                    {item.title}
                  </MenuTitle>
              )}
              {buildMenuArrow()}
            </MenuLink>
            <MenuSub
                className={clsx(
                    !item.collapse &&
                    'before:top-0 before:bottom-0 before:border-l before:border-gray-200',
                    '[&_.MuiCollapse-wrapperInner]:flex [&_.MuiCollapse-wrapperInner]:flex-col [&_.MuiCollapse-wrapperInner]:gap-0.5',
                    !item.collapse && accordionBorderLeft[level],
                    !item.collapse && accordionPl[level],
                    !item.collapse && 'relative before:absolute'
                )}
            >
              {buildMenuItemChildren(item.children as TMenuConfig, index, item.collapse ? level : level + 1)}
            </MenuSub>
          </MenuItem>
      );
    } else {
      return (
          <MenuItem key={index}>
            <MenuLink
                path={item.path}
                className={clsx(
                    'border border-transparent items-center grow menu-item-active:bg-secondary-active dark:menu-item-active:bg-coal-300 dark:menu-item-active:border-gray-100 menu-item-active:rounded-lg hover:bg-secondary-active dark:hover:bg-coal-300 dark:hover:border-gray-100 hover:rounded-lg',
                    accordionLinkGap[level],
                    accordionLinkPl,
                    linkPr,
                    subLinkPy
                )}
            >
              {buildMenuBullet()}
              <MenuTitle className="text-2sm font-medium text-gray-700 menu-item-active:text-primary menu-item-active:font-semibold menu-link-hover:!text-primary">
                {item.title}
              </MenuTitle>
            </MenuLink>
          </MenuItem>
      );
    }
  };

  const buildMenuItemChildDisabled = (item: ExtendedMenuItemConfig, index: number, level: number = 0) => {
    return (
        <MenuItem key={index}>
          <MenuLabel
              className={clsx(
                  'border border-transparent items-center grow cursor-default opacity-50',
                  accordionLinkGap[level],
                  accordionLinkPl,
                  linkPr,
                  subLinkPy
              )}
          >
            {buildMenuBullet()}
            <MenuTitle className="text-2sm font-medium text-gray-700">{item.title}</MenuTitle>
            {item.disabled && buildMenuDisabled()}
            {item.disabledReason && (
                <div className="text-xs text-red-500 mt-1">{item.disabledReason}</div>
            )}
          </MenuLabel>
        </MenuItem>
    );
  };

  const buildMenuHeading = (item: IMenuItemConfig, index: number) => {
    return (
        <MenuItem key={index} className="pt-2.25 pb-px">
          <MenuHeading className={clsx('uppercase text-2sm font-semibold text-gray-500', linkPl, linkPr)}>
            {item.heading}
          </MenuHeading>
        </MenuItem>
    );
  };

  const buildMenuArrow = () => {
    return (
        <MenuArrow className={clsx('text-gray-400 w-[20px] shrink-0 justify-end ms-1', rightOffset)}>
          <KeenIcon icon="plus" className="text-2xs menu-item-show:hidden" />
          <KeenIcon icon="minus" className="text-2xs hidden menu-item-show:inline-flex" />
        </MenuArrow>
    );
  };

  const buildMenuBullet = () => {
    return (
        <MenuBullet className="flex w-[6px] relative before:absolute before:top-0 before:size-[6px] before:rounded-full before:-translate-x-1/2 before:-translate-y-1/2 menu-item-active:before:bg-primary menu-item-hover:before:bg-primary"></MenuBullet>
    );
  };

  const buildMenuSoon = () => {
    return (
        <MenuBadge className={rightOffset}>
          <span className="badge badge-xs">Soon</span>
        </MenuBadge>
    );
  };

  const buildMenuDisabled = () => {
    return (
        <MenuBadge className={rightOffset}>
          <span className="badge badge-xs">Disabled</span>
        </MenuBadge>
    );
  };

  if (!menuConfig) {
    // Loading state while fetching menu configuration
    return <div>Loading menu...</div>;
  }

  return (
      <Menu highlight={true} multipleExpand={false} className="flex flex-col grow gap-0.5">
        {buildMenu(menuConfig)}
      </Menu>
  );
};

export { SidebarMenu };
